import { cn } from '@crowdex/ui/lib/utils';
import { Lock } from 'lucide-react';
import { useDesktopShellStore } from '@/lib/desktop-shell/store-context';
import { useCallDuration } from './call-clock';
import { liveChip, speakingAssistant, useLivePlace } from './live-place';
import type { LiveCall } from './use-live-call';

/** "Live · 2:14" while in a call, "Live" out of one; the dot says it is on. */
export function LiveClock({ call, suffix }: { call: LiveCall; suffix?: string }) {
  const duration = useCallDuration(call.status !== 'idle');
  return (
    <span className="flex shrink-0 items-center gap-1.5 font-medium">
      {duration !== null && <span aria-hidden className="size-2 rounded-full bg-destructive" />}
      {duration === null ? 'Live' : `Live · ${duration}`}
      {suffix !== undefined && <span className="font-normal">· {suffix}</span>}
    </span>
  );
}

/** Who the user talks to, as a chip: the assistant, in its colour. */
function LiveChip({ call, className }: { call: LiveCall; className?: string }) {
  const chip = liveChip(speakingAssistant(call));
  return (
    <span
      className={cn(
        'flex h-7 shrink-0 items-center gap-1.5 rounded-control bg-control px-2.5 font-medium',
        className,
      )}
      data-testid="live-chip"
    >
      {chip.color !== null && (
        <span
          aria-hidden
          className="size-2 rounded-[3px]"
          // The assistant's own colour, as its row in the sidebar.
          style={{ backgroundColor: chip.color }}
        />
      )}
      {chip.label}
    </span>
  );
}

/**
 * The call's line under its title (D28): where it stands, who the user talks to, what is on
 * screen (it comes first), and that it is private.
 */
export function LiveCallLine({ call, className }: { call: LiveCall; className?: string }) {
  const chatId = useDesktopShellStore((state) => state.chatId);
  const { onScreen, privacy } = useLivePlace(chatId);
  return (
    <div
      className={cn('type-meta flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1', className)}
    >
      <LiveClock call={call} />
      <LiveChip call={call} />
      {onScreen !== null && <span className="truncate text-muted-foreground">{onScreen}</span>}
      <span className="flex shrink-0 items-center gap-1 text-subtle-foreground">
        <Lock aria-hidden className="size-3" />
        {privacy}
      </span>
    </div>
  );
}
